import { useEffect, useMemo } from 'react';
import { getI18n } from 'react-i18next';
import { TicketSystemFieldsKey, customSWRConfig } from '../../model/types';
import { UserForPlaceholder } from '../../store/childStore/macroStore';
import { getMacroStore } from '../../utils/store/getStore';
import useConditionOptions from '../../utils/useConditionOptions';
import useAssignees from './useAssignees';

interface AssigneeCascaderOptionType {
    key?: string;
    value?: number | string;
    children?: AssigneeCascaderOptionType[];
    userinfo?: UserForPlaceholder;
    label?: string;
}

export default function useCascaderAssigneeList(config: customSWRConfig = {}): {
    options: AssigneeCascaderOptionType[];
    optionsWithEmptyOption: AssigneeCascaderOptionType[];
    optionsForCondition: AssigneeCascaderOptionType[];
    optionsForAction: AssigneeCascaderOptionType[];
} {
    const { data: assignees } = useAssignees(config);
    const { localizeOptions } = useConditionOptions({ triggers: true });
    const options = useMemo(() => {
        const cascaderAssigneeList = assignees?.map((group) => {
            return {
                value: group.id,
                label: group.name,
                children:
                    group.users?.map((user) => {
                        return {
                            key: user.email,
                            value: user.id,
                            children: [],
                            userinfo: user,
                            label: `${!user.enabled ? getI18n().t('disabledForm', { ns: 'forms' }) : ''} ${
                                user.fullName
                            } (${user.email})`,
                            disabled: !user.enabled
                        };
                    }) ?? []
            };
        });

        return localizeOptions(TicketSystemFieldsKey.assignee, cascaderAssigneeList as any);
    }, [assignees, localizeOptions]);

    const optionsWithEmptyOption = useMemo(
        () => [
            ...localizeOptions(TicketSystemFieldsKey.assignee, [{ label: '-', value: ':notAssigned' } as any]),
            ...(options || [])
        ],
        [options, localizeOptions]
    );

    const optionsForCondition = useMemo(
        () => [
            ...localizeOptions(TicketSystemFieldsKey.assignee, [
                { label: '-', value: ':notAssigned' } as any,
                { label: '(currentUser)', value: ':currentUser' } as any,
                { label: '(requester)', value: ':requester' } as any
            ]),
            ...(options || [])
        ],
        [options, localizeOptions]
    );
    const optionsForAction = useMemo(
        () => [
            ...localizeOptions(TicketSystemFieldsKey.assignee, [
                { label: '-', value: ':notAssigned' } as any,
                { label: '(currentUser)', value: ':currentUser' } as any
            ]),
            ...(options || [])
        ],
        [options, localizeOptions]
    );
    useEffect(() => {
        options && getMacroStore().setAssigneeOptions(options);
    }, [options]);

    return { options, optionsWithEmptyOption, optionsForCondition, optionsForAction };
}

export { type AssigneeCascaderOptionType };
